import { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Star, Edit2, Save, X } from 'lucide-react';

interface Student {
  id: string;
  first_name: string;
  last_name: string;
}

interface PhaseGrade {
  id: string;
  student_id: string;
  grade: number;
  comment?: string;
  lecturer_id: string;
}

interface GradeCellProps {
  student: Student;
  grade?: PhaseGrade;
  onSaveGrade: (studentId: string, grade: number, comment?: string) => Promise<void>;
}

export const GradeCell = ({ student, grade, onSaveGrade }: GradeCellProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [gradeValue, setGradeValue] = useState(grade?.grade?.toString() || '');
  const [comment, setComment] = useState(grade?.comment || '');
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    const numericGrade = parseInt(gradeValue);
    
    if (!gradeValue || numericGrade < 1 || numericGrade > 10) {
      return;
    }

    setIsSaving(true);
    try {
      await onSaveGrade(student.id, numericGrade, comment.trim() || undefined);
      setIsDialogOpen(false);
    } catch (error) {
      console.error('Error saving grade:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setGradeValue(grade?.grade?.toString() || '');
    setComment(grade?.comment || '');
    setIsDialogOpen(false);
  };

  const getGradeColor = (gradeNum: number) => {
    if (gradeNum >= 9) return 'text-primary border-border/70 bg-primary/10';
    if (gradeNum >= 7) return 'text-yellow-400 border-yellow-400 bg-yellow-500/20';
    if (gradeNum >= 5) return 'text-orange-400 border-orange-400 bg-orange-500/20';
    return 'text-destructive border-destructive/40 bg-destructive/10';
  };

  if (grade) {
    return (
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogTrigger asChild>
          <button className="w-full">
            <Badge 
              variant="outline" 
              className={`text-lg font-bold py-2 px-3 cursor-pointer hover:opacity-80 transition-opacity ${getGradeColor(grade.grade)}`}
            >
              <Star className="h-4 w-4 mr-1" />
              {grade.grade}/10
            </Badge>
          </button>
        </DialogTrigger>
        <DialogContent className="bg-muted border-border/70">
          <DialogHeader>
            <DialogTitle className="text-muted-foreground font-mono">
              Գնահատական - {student.first_name} {student.last_name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-muted-foreground font-mono text-sm mb-2 block">
                Գնահատական (1-10)
              </label>
              <Input
                type="number"
                min="1"
                max="10"
                value={gradeValue}
                onChange={(e) => setGradeValue(e.target.value)}
                className="bg-muted border-border/70 text-foreground font-mono"
                placeholder="1-10"
              />
            </div>
            
            <div>
              <label className="text-muted-foreground font-mono text-sm mb-2 block">
                Մեկնաբանություն (ծրագրային)
              </label>
              <Textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="bg-muted border-border/70 text-foreground font-mono"
                placeholder="Գնահատականի մեկնաբանություն..."
                rows={3}
              />
            </div>
            
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={handleCancel}
                className="text-muted-foreground border-border"
              >
                <X className="h-4 w-4 mr-1" />
                Չեղարկել
              </Button>
              <Button
                onClick={handleSave}
                disabled={isSaving || !gradeValue || parseInt(gradeValue) < 1 || parseInt(gradeValue) > 10}
                className="bg-primary hover:bg-primary/90"
              >
                <Save className="h-4 w-4 mr-1" />
                Պահպանել
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger asChild>
        <button className="w-full p-2 border-2 border-dashed border-amber-500/40 rounded-lg hover:border-amber-500/40 hover:bg-amber-500/10 transition-all duration-200 text-amber-400">
          <div className="flex flex-col items-center gap-1">
            <Star className="h-5 w-5" />
            <span className="text-xs font-mono">Գնահատել</span>
          </div>
        </button>
      </DialogTrigger>
      <DialogContent className="bg-muted border-border/70">
        <DialogHeader>
          <DialogTitle className="text-muted-foreground font-mono">
            Գնահատական - {student.first_name} {student.last_name}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div>
            <label className="text-muted-foreground font-mono text-sm mb-2 block">
              Գնահատական (1-10)
            </label>
            <Input
              type="number"
              min="1"
              max="10"
              value={gradeValue}
              onChange={(e) => setGradeValue(e.target.value)}
              className="bg-muted border-border/70 text-foreground font-mono"
              placeholder="1-10"
            />
          </div>
          
          <div>
            <label className="text-muted-foreground font-mono text-sm mb-2 block">
              Մեկնաբանություն (ծրագրային)
            </label>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="bg-muted border-border/70 text-foreground font-mono"
              placeholder="Գնահատականի մեկնաբանություն..."
              rows={3}
            />
          </div>
          
          <div className="flex gap-2 justify-end">
            <Button
              variant="outline"
              onClick={handleCancel}
              className="text-muted-foreground border-border"
            >
              <X className="h-4 w-4 mr-1" />
              Չեղարկել
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSaving || !gradeValue || parseInt(gradeValue) < 1 || parseInt(gradeValue) > 10}
              className="bg-primary hover:bg-primary/90"
            >
              <Save className="h-4 w-4 mr-1" />
              Պահպանել
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};